import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { LoaderComponent } from "../../../Common/LoaderComponent";
import { LEDGER_DEPARTMENTS } from "./StockLedgerReport";

const MEASURE_LABELS = {
  qty: "Qty",
  export: "Export",
  a: "A Class",
  b: "B Class",
  c: "C Class",
  inner: "Inner",
  master: "Master",
};

const tdClass = "text-uppercase text-dark text-sm font-weight-bolder ps-2";
const thClass =
  "text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center";

const StockLedgerReconcile = () => {
  const location = useLocation();
  const [alert, setAlert] = useState(null);
  const [department, setDepartment] = useState(8);
  const [result, setResult] = useState(null);
  const [search, setSearch] = useState("");

  const showAlert = (message, type) => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 4000);
  };

  useEffect(() => {
    if (!location.state) return;
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const runReconcile = () => {
    setResult(null);
    Http.get(`/stock-ledger/reconcile?department_id=${department}`)
      .then((res) => setResult(res.data))
      .catch((err) =>
        showAlert(
          err?.response?.data?.message || "Reconcile failed",
          "danger",
        ),
      );
  };

  const downloadCsv = () => {
    if (!result) return;
    const lines = [
      ["Product Code", "Measure", "Ledger", "Calculator", "Difference"],
      ...result.mismatches.map((m) => [
        m.product_code,
        MEASURE_LABELS[m.measure] || m.measure,
        m.ledger,
        m.calculator,
        m.diff,
      ]),
    ];
    const csv = lines
      .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `stock-ledger-reconcile-${department}.csv`;
    a.click();
  };

  const rows = (result?.mismatches || []).filter((m) =>
    search.trim()
      ? String(m.product_code || "")
          .toLowerCase()
          .includes(search.trim().toLowerCase())
      : true,
  );

  const departmentName =
    LEDGER_DEPARTMENTS.find((d) => d.id === Number(department))?.name || "";

  return (
    <div>
      <IsLogin></IsLogin>
      <LoaderComponent />
      <div>
        <div className="am-mainpanel">
          <div className="am-pagetitle">
            <h5 className="am-title">Stock Ledger Reconcile</h5>
          </div>
          <div className="am-pagebody">
            <Alert alert={alert} />
            <div className="card pd-20 pd-sm-40">
              <div className="row">
                <div className="col-md-4">
                  <label className="form-control-label">Department</label>
                  <select
                    className="form-control"
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                  >
                    {LEDGER_DEPARTMENTS.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-control-label">
                    Filter Product Code
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search mismatches"
                  />
                </div>
                <div className="col-md-4 d-flex align-items-end">
                  <button
                    type="button"
                    className="btn btn-primary mr-2"
                    onClick={runReconcile}
                  >
                    Run Reconcile
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={downloadCsv}
                    disabled={!result || result.mismatch_count === 0}
                  >
                    CSV
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="am-pagebody">
            <div className="card pd-20 pd-sm-40">
              <div className="text-center">
                <h5 className="text-uppercase text-sm font-weight-bolder">
                  Artisan Ceramics Limited
                </h5>
                <p>
                  <b>Stock Ledger vs Calculator Reconcile</b>
                  <br />({departmentName})
                </p>
              </div>

              {!result && (
                <p className="text-center">
                  Select a department and click Run Reconcile
                </p>
              )}

              {result && (
                <>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="bg-light p-2 border">
                        Products checked: <b>{result.products_checked}</b>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div
                        className={`p-2 border ${
                          result.mismatch_count === 0
                            ? "bg-success text-white"
                            : "bg-warning"
                        }`}
                      >
                        Mismatches: <b>{result.mismatch_count}</b>
                        {result.mismatch_count === 0 && " — all matched"}
                      </div>
                    </div>
                  </div>

                  <div className="table-responsive p-0 pb-2">
                    <table className="table table-sm table-bordered table-striped align-items-center justify-content-center mb-0">
                      <thead>
                        <tr>
                          <th className={thClass}>Product Code</th>
                          <th className={thClass}>Measure</th>
                          <th className={thClass}>Ledger</th>
                          <th className={thClass}>Calculator</th>
                          <th className={thClass}>Difference</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length > 0 ? (
                          rows.map((m, i) => (
                            <tr key={i}>
                              <td className={tdClass}>{m.product_code}</td>
                              <td className={tdClass}>
                                {MEASURE_LABELS[m.measure] || m.measure}
                              </td>
                              <td className={tdClass}>{m.ledger}</td>
                              <td className={tdClass}>{m.calculator}</td>
                              <td
                                className={`${tdClass} ${
                                  m.diff > 0 ? "text-success" : "text-danger"
                                }`}
                              >
                                {m.diff}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td className={`${tdClass} text-center`} colSpan={5}>
                              No mismatches found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockLedgerReconcile;